import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  LineChart,
  Line
} from 'recharts';
import {
  Download,
  Warning,
  Error,
  Inventory2,
  ArrowBack,
  Refresh,
  TrendingUp,
  TrendingDown,
  Equalizer,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  ShowChart,
  CalendarToday,
  Info,
  LocalShipping,
  Assessment,
  Speed
} from '@mui/icons-material';
import reportService from '../../../services/reportService';
import excelExport from '../../../utils/excelExport';
import DateRangeFilter from './DateRangeFilter';
import './InventoryReports.css';

// Currency formatter for LKR
const formatLKR = (amount) => {
  if (amount === null || amount === undefined) return 'LKR 0';
  return `LKR ${parseFloat(amount).toLocaleString('en-LK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

const formatNumber = (number) => {
  if (number === null || number === undefined) return '0';
  return number.toLocaleString('en-LK');
};

const InventoryReports = ({ onPageChange }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportType, setReportType] = useState('low_stock_alerts');
  const [reportData, setReportData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filters, setFilters] = useState({
    days: 30
  });

  const reportTypes = [
    { 
      value: 'low_stock_alerts', 
      label: 'Low Stock Alerts', 
      icon: <Warning />,
      description: 'Products running low on stock'
    },
    { 
      value: 'inventory', 
      label: 'Stock vs Sales', 
      icon: <Assessment />,
      description: 'Inventory performance vs sales data'
    },
    { 
      value: 'products_needing_reorder', 
      label: 'Reorder Recommendations', 
      icon: <LocalShipping />,
      description: 'Products that need immediate reordering'
    },
    { 
      value: 'stock_valuation', 
      label: 'Stock Valuation', 
      icon: <Speed />,
      description: 'Total inventory value and cost analysis'
    }
  ];

  // Color schemes
  const chartColors = {
    critical: '#EF4444',
    high: '#F59E0B',
    medium: '#FBBF24',
    low: '#10B981',
    cost: '#6366F1',
    retail: '#10B981',
    profit: '#8B5CF6',
    stock: '#3B82F6',
    sales: '#EC4899'
  };

  useEffect(() => {
    loadReportData();
  }, [reportType, filters]);

  const loadReportData = async () => {
    setLoading(true);
    setError('');
    try {
      let response;
      switch (reportType) {
        case 'low_stock_alerts':
          response = await reportService.getLowStockAlerts();
          break;
        case 'inventory':
          response = await reportService.getStockVsSales(filters);
          break;
        case 'products_needing_reorder':
          response = await reportService.getProductsNeedingReorder();
          break;
        case 'stock_valuation':
          response = await reportService.getStockValuation();
          break;
        default:
          response = await reportService.getLowStockAlerts();
      }
      setReportData(response.data);
      setLastUpdated(new Date());
    } catch (err) {
      setError('Failed to load inventory report data: ' + (err.message || 'Unknown error'));
      console.error('Inventory report loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!reportData) return;

    try {
      switch (reportType) {
        case 'low_stock_alerts':
          excelExport.exportLowStockAlerts(reportData);
          break;
        case 'inventory':
          excelExport.exportInventory(reportData);
          break;
        case 'stock_valuation':
          excelExport.exportStockValuation(reportData);
          break;
        case 'products_needing_reorder':
          const reorderData = reportData.reorder_products?.map(product => ({
            'Product Name': product.name,
            'SKU': product.sku,
            'Brand': product.brand,
            'Current Stock': product.current_stock,
            'Low Stock Threshold': product.low_stock_threshold,
            'Recommended Reorder Quantity': product.recommended_reorder_quantity,
            'Reorder Cost': product.reorder_cost,
            'Priority': product.priority,
            'Category': product.category
          })) || [];

          if (reorderData.length > 0) {
            excelExport.exportToExcel(reorderData, 'Reorder_Recommendations');
          } else {
            setError('No reorder data available for export');
          }
          break;
        default:
          const exportData = Object.entries(reportData)
            .filter(([key, value]) => Array.isArray(value) && value.length > 0)
            .flatMap(([key, value]) => value);

          if (exportData.length > 0) {
            excelExport.exportToExcel(exportData, `${reportType}_report`);
          } else {
            setError('No data available for export');
          }
      }
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export data: ' + err.message);
    }
  };

  const renderMetricCard = (title, value, subtitle = '', format = 'number', color = 'primary') => {
    const formattedValue = format === 'currency' ? formatLKR(value) : formatNumber(value);

    return (
      <Card className="inv-metric-card">
        <CardContent className="inv-metric-content">
          <div className="inv-metric-header">
            <Typography className="inv-metric-title">
              {title}
            </Typography>
            {subtitle && (
              <Tooltip title={subtitle}>
                <Info className="inv-metric-info" />
              </Tooltip>
            )}
          </div>
          <Typography className={`inv-metric-value inv-metric-${color}`}>
            {formattedValue}
          </Typography>
        </CardContent>
      </Card>
    );
  };

  const renderLowStockAlerts = () => {
    if (!reportData?.low_stock_products) return null;

    const urgencyData = [
      { name: 'Critical', value: reportData.low_stock_products.filter(p => p.urgency === 'critical').length, color: chartColors.critical },
      { name: 'High', value: reportData.low_stock_products.filter(p => p.urgency === 'high').length, color: chartColors.high },
      { name: 'Medium', value: reportData.low_stock_products.filter(p => p.urgency === 'medium').length, color: chartColors.medium }
    ];

    return (
      <div className="inv-report-container">
        {/* Summary Metrics */}
        <div className="inv-metrics-grid">
          {renderMetricCard(
            'Total Low Stock',
            reportData.summary?.total_low_stock || 0,
            'Products below stock threshold',
            'number',
            'warning'
          )}
          {renderMetricCard(
            'Critical Alerts',
            reportData.summary?.critical_alerts || 0,
            'Products with critical stock levels',
            'number',
            'error'
          )}
          {renderMetricCard(
            'Total Stock Value',
            reportData.summary?.total_stock_value || 0,
            'Total value of low stock items',
            'currency',
            'info'
          )}
          {renderMetricCard(
            'Avg. Stock Level',
            reportData.summary?.average_stock_level || 0,
            'Average stock level across low stock items',
            'number',
            'primary'
          )}
        </div>

        {/* Charts and Visualizations */}
        <div className="inv-charts-row">
          <Card className="inv-chart-card">
            <CardContent className="inv-chart-content">
              <div className="inv-chart-header">
                <PieChartIcon className="inv-chart-icon" />
                <Typography className="inv-chart-title">Urgency Distribution</Typography>
              </div>
              <div className="inv-chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart className="inv-pie-chart">
                    <Pie
                      data={urgencyData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {urgencyData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip className="inv-chart-tooltip" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="inv-chart-card">
            <CardContent className="inv-chart-content">
              <div className="inv-chart-header">
                <BarChartIcon className="inv-chart-icon" />
                <Typography className="inv-chart-title">Stock Status Overview</Typography>
              </div>
              <div className="inv-status-overview">
                {urgencyData.map((item) => (
                  <div key={item.name} className="inv-status-item">
                    <div className="inv-status-color" style={{ backgroundColor: item.color }} />
                    <Typography className="inv-status-label">{item.name} Priority</Typography>
                    <Chip 
                      label={item.value} 
                      className="inv-status-count"
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Low Stock Products Table */}
        <Card className="inv-table-card">
          <CardContent className="inv-table-content">
            <div className="inv-table-header">
              <Warning className="inv-table-icon" />
              <Typography className="inv-table-title">
                Low Stock Products ({reportData.low_stock_products.length})
              </Typography>
            </div>
            <TableContainer className="inv-table-container">
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell className="inv-table-header-cell">Product</TableCell>
                    <TableCell className="inv-table-header-cell">SKU</TableCell>
                    <TableCell align="right" className="inv-table-header-cell">Current Stock</TableCell>
                    <TableCell align="right" className="inv-table-header-cell">Threshold</TableCell>
                    <TableCell className="inv-table-header-cell">Urgency</TableCell>
                    <TableCell className="inv-table-header-cell">Category</TableCell>
                    <TableCell align="right" className="inv-table-header-cell">Stock Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.low_stock_products.map((product) => (
                    <TableRow key={product.id} className="inv-table-row">
                      <TableCell>
                        <Typography className="inv-product-name">{product.name}</Typography>
                        <Typography className="inv-product-brand">{product.brand}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={product.sku} className="inv-sku-chip" />
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={formatNumber(product.current_stock)}
                          className={`inv-stock-chip inv-urgency-${product.urgency}`}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(product.low_stock_threshold)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={product.urgency?.toUpperCase()}
                          className={`inv-urgency-chip inv-${product.urgency}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography className="inv-category-text">
                          {product.category}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography className="inv-value-text">
                          {formatLKR(product.stock_value)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderStockValuation = () => {
    if (!reportData?.overall_valuation) return null;

    return (
      <div className="inv-report-container">
        {/* Valuation Metrics */}
        <div className="inv-metrics-grid">
          {renderMetricCard(
            'Total Products',
            reportData.overall_valuation.total_products,
            'Total number of products in inventory',
            'number',
            'primary'
          )}
          {renderMetricCard(
            'Total Quantity',
            reportData.overall_valuation.total_quantity,
            'Total stock quantity across all products',
            'number',
            'info'
          )}
          {renderMetricCard(
            'Cost Value',
            reportData.overall_valuation.total_cost_value,
            'Total inventory cost value',
            'currency',
            'primary'
          )}
          {renderMetricCard(
            'Retail Value',
            reportData.overall_valuation.total_retail_value,
            'Total inventory retail value',
            'currency',
            'success'
          )}
        </div>

        {/* Valuation Chart */}
        <div className="inv-charts-row">
          <Card className="inv-chart-card inv-chart-wide">
            <CardContent className="inv-chart-content">
              <div className="inv-chart-header">
                <BarChartIcon className="inv-chart-icon" />
                <Typography className="inv-chart-title">Valuation by Category</Typography>
              </div>
              <div className="inv-chart-container">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={reportData.valuation_by_category} className="inv-bar-chart">
                    <CartesianGrid className="inv-chart-grid" />
                    <XAxis dataKey="category_name" className="inv-chart-axis" />
                    <YAxis 
                      tickFormatter={(value) => `LKR ${value / 1000}k`}
                      className="inv-chart-axis"
                    />
                    <RechartsTooltip 
                      formatter={(value) => [formatLKR(value), 'Value']}
                      className="inv-chart-tooltip"
                    />
                    <Legend className="inv-chart-legend" />
                    <Bar 
                      dataKey="total_cost_value" 
                      fill={chartColors.cost} 
                      name="Cost Value"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar 
                      dataKey="total_retail_value" 
                      fill={chartColors.retail} 
                      name="Retail Value"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="inv-side-card">
            <CardContent className="inv-side-content">
              <div className="inv-side-header">
                <TrendingUp className="inv-side-icon" />
                <Typography className="inv-side-title">Profit Analysis</Typography>
              </div>
              <div className="inv-side-metrics">
                <div className="inv-side-metric">
                  <Typography className="inv-side-label">Potential Profit</Typography>
                  <Typography className="inv-side-value inv-profit-value">
                    {formatLKR(reportData.overall_valuation.potential_profit)}
                  </Typography>
                </div>
                <div className="inv-side-metric">
                  <Typography className="inv-side-label">Profit Margin</Typography>
                  <Typography className="inv-side-value inv-margin-value">
                    {reportData.overall_valuation.profit_margin_percentage?.toFixed(2)}%
                  </Typography>
                </div>
                <div className="inv-side-metric">
                  <Typography className="inv-side-label">Avg Cost per Item</Typography>
                  <Typography className="inv-side-text">
                    {formatLKR(reportData.overall_valuation.average_cost_per_item)}
                  </Typography>
                </div>
                <div className="inv-side-metric">
                  <Typography className="inv-side-label">Avg Retail per Item</Typography>
                  <Typography className="inv-side-text inv-retail-text">
                    {formatLKR(reportData.overall_valuation.average_retail_per_item)}
                  </Typography>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Top Valued Products */}
        <Card className="inv-table-card">
          <CardContent className="inv-table-content">
            <div className="inv-table-header">
              <Speed className="inv-table-icon" />
              <Typography className="inv-table-title">Top Valued Products</Typography>
            </div>
            <TableContainer className="inv-table-container">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell className="inv-table-header-cell">Product</TableCell>
                    <TableCell className="inv-table-header-cell">SKU</TableCell>
                    <TableCell align="right" className="inv-table-header-cell">Stock Quantity</TableCell>
                    <TableCell align="right" className="inv-table-header-cell">Cost Price</TableCell>
                    <TableCell align="right" className="inv-table-header-cell">Retail Price</TableCell>
                    <TableCell align="right" className="inv-table-header-cell">Cost Value</TableCell>
                    <TableCell align="right" className="inv-table-header-cell">Retail Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.top_valued_products?.map((product) => (
                    <TableRow key={product.id} className="inv-table-row">
                      <TableCell>
                        <Typography className="inv-product-name">{product.name}</Typography>
                        <Typography className="inv-product-category">{product.category}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={product.sku} className="inv-sku-chip" />
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={formatNumber(product.stock_quantity)} 
                          className="inv-stock-chip"
                        />
                      </TableCell>
                      <TableCell align="right">
                        {formatLKR(product.cost_price)}
                      </TableCell>
                      <TableCell align="right">
                        {formatLKR(product.price)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography className="inv-cost-value">
                          {formatLKR(product.cost_value)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography className="inv-retail-value">
                          {formatLKR(product.retail_value)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderStockVsSales = () => {
    if (!reportData?.products) return null;

    return (
      <div className="inv-report-container">
        <Card className="inv-table-card">
          <CardContent className="inv-table-content">
            <div className="inv-table-header">
              <Assessment className="inv-table-icon" />
              <Typography className="inv-table-title">
                Stock vs Sales Analysis ({reportData.analysis_period_days} days)
              </Typography>
            </div>
            <TableContainer className="inv-table-container">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell className="inv-table-header-cell">Product</TableCell>
                    <TableCell className="inv-table-header-cell">SKU</TableCell>
                    <TableCell align="right" className="inv-table-header-cell">Current Stock</TableCell>
                    <TableCell align="right" className="inv-table-header-cell">Sold (30 days)</TableCell>
                    <TableCell align="right" className="inv-table-header-cell">Daily Sales Rate</TableCell>
                    <TableCell align="right" className="inv-table-header-cell">Days of Stock</TableCell>
                    <TableCell className="inv-table-header-cell">Stock Status</TableCell>
                    <TableCell className="inv-table-header-cell">Needs Reorder</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.products.map((product) => (
                    <TableRow key={product.id} className="inv-table-row">
                      <TableCell>
                        <Typography className="inv-product-name">{product.name}</Typography>
                        <Typography className="inv-product-category">{product.category}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={product.sku} className="inv-sku-chip" />
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={formatNumber(product.current_stock)} 
                          className={`inv-stock-chip inv-status-${product.sales_data.stock_status}`}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(product.sales_data.sold_last_30_days)}
                      </TableCell>
                      <TableCell align="right">
                        {product.sales_data.daily_sales_rate}
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={formatNumber(product.sales_data.days_of_stock_remaining)} 
                          className={`inv-days-chip inv-days-${product.sales_data.days_of_stock_remaining < 15 ? 'low' : product.sales_data.days_of_stock_remaining < 30 ? 'medium' : 'high'}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={product.sales_data.stock_status?.toUpperCase()}
                          className={`inv-status-chip inv-${product.sales_data.stock_status}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={product.needs_reorder ? 'YES' : 'NO'}
                          className={`inv-reorder-chip ${product.needs_reorder ? 'inv-reorder-yes' : 'inv-reorder-no'}`}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderProductsNeedingReorder = () => {
    if (!reportData?.reorder_products) return null;

    return (
      <div className="inv-report-container">
        {/* Summary Metrics */}
        <div className="inv-metrics-grid">
          {renderMetricCard(
            'Critical Priority',
            reportData.summary?.critical_priority || 0,
            'Products needing immediate attention',
            'number',
            'error'
          )}
          {renderMetricCard(
            'High Priority',
            reportData.summary?.high_priority || 0,
            'Products needing urgent reordering',
            'number',
            'warning'
          )}
          {renderMetricCard(
            'Total Products',
            reportData.summary?.total_products_needing_reorder || 0,
            'Total products needing reorder',
            'number',
            'info'
          )}
          {renderMetricCard(
            'Total Cost',
            reportData.summary?.total_reorder_cost || 0,
            'Total estimated reorder cost',
            'currency',
            'primary'
          )}
        </div>

        {/* Reorder Products Table */}
        <Card className="inv-table-card">
          <CardContent className="inv-table-content">
            <div className="inv-table-header">
              <LocalShipping className="inv-table-icon" />
              <Typography className="inv-table-title">
                Products Needing Reorder ({reportData.reorder_products.length})
              </Typography>
            </div>
            <TableContainer className="inv-table-container">
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell className="inv-table-header-cell">Product</TableCell>
                    <TableCell className="inv-table-header-cell">SKU</TableCell>
                    <TableCell className="inv-table-header-cell">Brand</TableCell>
                    <TableCell align="right" className="inv-table-header-cell">Current Stock</TableCell>
                    <TableCell align="right" className="inv-table-header-cell">Threshold</TableCell>
                    <TableCell align="right" className="inv-table-header-cell">Reorder Qty</TableCell>
                    <TableCell align="right" className="inv-table-header-cell">Reorder Cost</TableCell>
                    <TableCell className="inv-table-header-cell">Priority</TableCell>
                    <TableCell className="inv-table-header-cell">Category</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.reorder_products.map((product) => (
                    <TableRow key={product.id} className="inv-table-row">
                      <TableCell>
                        <Typography className="inv-product-name">{product.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={product.sku} className="inv-sku-chip" />
                      </TableCell>
                      <TableCell>
                        <Typography className="inv-brand-text">{product.brand}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={formatNumber(product.current_stock)}
                          className={`inv-stock-chip inv-priority-${product.priority}`}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(product.low_stock_threshold)}
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={formatNumber(product.recommended_reorder_quantity)} 
                          className="inv-reorder-qty-chip"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography className="inv-cost-text">
                          {formatLKR(product.reorder_cost)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={product.priority?.toUpperCase()}
                          className={`inv-priority-chip inv-${product.priority}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography className="inv-category-text">
                          {product.category}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderReportContent = () => {
    switch (reportType) {
      case 'low_stock_alerts':
        return renderLowStockAlerts();
      case 'stock_valuation':
        return renderStockValuation();
      case 'inventory':
        return renderStockVsSales();
      case 'products_needing_reorder':
        return renderProductsNeedingReorder();
      default:
        return renderLowStockAlerts();
    }
  };

  const currentReport = reportTypes.find(r => r.value === reportType);

  return (
    <Box className="inv-reports-container">
      {/* Header */}
      <Card className="inv-header-card">
        <CardContent className="inv-header-content">
          <div className="inv-header-main">
            <Button
              startIcon={<ArrowBack />}
              onClick={() => onPageChange('all-reports')}
              className="inv-back-button"
            >
              Back to Reports
            </Button>
            <div className="inv-header-text">
              <Typography className="inv-header-title">Inventory Analytics</Typography>
              <Typography className="inv-header-subtitle">
                {currentReport?.description}
              </Typography>
            </div>
          </div>
          <div className="inv-header-actions">
            <Tooltip title="Refresh Data">
              <IconButton 
                onClick={loadReportData} 
                disabled={loading}
                className="inv-refresh-button"
              >
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button
              startIcon={<Download />}
              onClick={handleExport}
              disabled={!reportData || loading}
              className="inv-export-button"
            >
              Export Excel
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Report Type Selection */}
      <Card className="inv-controls-card">
        <CardContent className="inv-controls-content">
          <Typography className="inv-controls-title">Report Type</Typography>
          <div className="inv-controls-grid">
            <FormControl fullWidth className="inv-select-control">
              <InputLabel>Select Report</InputLabel>
              <Select
                value={reportType}
                label="Select Report"
                onChange={(e) => setReportType(e.target.value)}
              >
                {reportTypes.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    <div className="inv-menu-item">
                      {type.icon}
                      {type.label}
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {(reportType === 'inventory') && (
              <DateRangeFilter
                filters={filters}
                onFilterChange={setFilters}
                showDays={true}
              />
            )}
          </div>
          {lastUpdated && (
            <div className="inv-last-updated">
              <Typography className="inv-updated-text">
                Last updated: {lastUpdated.toLocaleString()}
              </Typography>
            </div>
          )}
        </CardContent>
      </Card>

      {error && (
        <Alert 
          severity="error" 
          className="inv-error-alert"
          onClose={() => setError('')}
          action={
            <Button color="inherit" size="small" onClick={loadReportData}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="inv-loading-container">
          <CircularProgress size={60} className="inv-loading-spinner" />
          <Typography className="inv-loading-text">
            Loading inventory data...
          </Typography>
        </div>
      ) : reportData ? (
        renderReportContent()
      ) : (
        <Card className="inv-empty-card">
          <CardContent className="inv-empty-content">
            <Typography className="inv-empty-text">
              No inventory data available for the selected report type.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default InventoryReports;